import { Schema } from "effect";
import { Severity } from "./severity.ts";

// The `##` section headings of the curriculum-markdown structure spec, verbatim. A finding's
// `specSection` is the agent-facing citation: the agent that receives it has the spec bundled in its
// `reference-library` skill (ADR-0055) and resolves the citation by grepping that file, so the
// citation has to be a string that occurs in it. It previously read "spec rule 14" — a row number
// from the plan's Appendix table, which the agent does not have and the spec does not number, so
// every citation the package emitted was unresolvable at the only place it was meant to be resolved.
// `curriculum-md`'s `test/spec-coverage.test.ts` greps each literal below out of the bundled spec, so
// a spec retitling fails there rather than silently degrading every finding into an unresolvable
// pointer.
export const SPEC_SECTIONS = {
  phase1: "## Phase 1 — skeleton (no `##### Authored Exercises` yet)",
  phase2: "## Phase 2 — populated (adds `##### Authored Exercises`)",
  appendix: "## Appendix (optional, terminal)",
  formatting: "## Formatting invariants (apply across both phases)",
  readers: "## Readers",
} as const;

export const SpecSection = Schema.Literal(
  SPEC_SECTIONS.phase1,
  SPEC_SECTIONS.phase2,
  SPEC_SECTIONS.appendix,
  SPEC_SECTIONS.formatting,
  SPEC_SECTIONS.readers,
);
export type SpecSection = typeof SpecSection.Type;

// The `##` section headings of `docs/exercise-audit-checks.md`, verbatim — the citation vocabulary
// for the audit rules (`curriculum-check`'s `rules/exercise-audits.ts` and
// `rules/curriculum-audits.ts`), which enforce content/pedagogy constraints the
// curriculum-markdown structure spec never claimed to cover.
//
// A separate constant from `SPEC_SECTIONS` rather than four more members of it, because the two
// resolve in DIFFERENT documents: a `SPEC_SECTIONS` value is greppable in the bundled
// `curriculum-markdown-structure-spec.md`, an `AUDIT_SECTIONS` value in the monorepo-root audit
// inventory. Folding them together would make `spec-coverage.test.ts`'s "every `##` heading of the
// bundled spec" equality unsatisfiable, which is the check that makes a citation worth emitting at
// all. `curriculum-check`'s `test/audit-coverage.test.ts` greps each literal below out of the
// inventory, so a retitling there fails loudly rather than degrading every audit finding into a
// dead pointer — the same anti-rot guarantee, pointed at the second document.
//
// Deliberately coarse: the inventory organises checks into registry-scoped tables, not one heading
// per check, so a rule cites the TABLE its row lives in. The row itself is found by the rule's
// `docs` sentence, which restates the constraint verbatim.
export const AUDIT_SECTIONS = {
  perRecord: "## 1. `CHECKS` — per-record, always run",
  withCurriculum: "## 2. `CHECKS_WITH_CURRICULUM` — need `curriculum_unit_plans.json`",
  structural: "## 4. Structural sub-checks (from the retired `run_all`)",
  curriculum: "## 6. `CURRICULUM_CHECKS` — curriculum (unit-plan) audit",
  languageChecks: "## 7. Language checks a pack decides (`checks.yml`)",
} as const;

export const AuditSection = Schema.Literal(
  AUDIT_SECTIONS.perRecord,
  AUDIT_SECTIONS.withCurriculum,
  AUDIT_SECTIONS.structural,
  AUDIT_SECTIONS.curriculum,
  AUDIT_SECTIONS.languageChecks,
);
export type AuditSection = typeof AuditSection.Type;

// What a `Finding` may cite. Widened from `SpecSection` alone so a deterministic rule sourced from
// the audit inventory can cite honestly instead of picking the least-wrong spec heading — the exact
// "unresolvable pointer" failure the `SPEC_SECTIONS` comment above records. Widening the VALUE set
// while every value still has to be greppable in a real document keeps that guarantee intact; what
// it does not survive is a citation with no document behind it at all.
export const Citation = Schema.Union(SpecSection, AuditSection);
export type Citation = typeof Citation.Type;

// Every finding id `lintCurriculum` can emit, across all three doors it emits through (U4 retired
// the fourth — the wire `Schema` decode — along with the whole rule-running/lint surface, which now
// lives in `@curriculum-studio/curriculum-check`; see that unit's report). `RULES` is the only door
// that looks like a registry, so a consumer needing a TOTAL mapping over finding ids — a severity or
// noise-downgrade table on the diagnostics rail — naturally keys off it and silently misses the other
// two ids. That is not hypothetical: linting `A1_2_combined_master` emits 27 `MISSING_REQUIRED_FIELD`
// findings, none of them in `RULES`.
//
// Hand-listed here rather than derived, and that is the point: `Rule.id` and `Finding.ruleId` are both
// typed as `FindingId`, so a rule registered under an id absent from this array does not compile, and
// `curriculum-check`'s `test/spec-coverage.test.ts` asserts this array is exactly the union of the
// three doors' ids. The escape this closes already happened once — `STRUCTURAL_ORDER` appeared in
// neither `RULES` nor the coverage table (see `lint.ts`'s `PARSE_BOUNDARY_RULE_IDS`).
export const ALL_FINDING_IDS = [
  // Door 1 — the `RULES` registry (`rules/structure.ts`, `rules/labels.ts`, `rules/exercise-layer.ts`).
  "SINGLE_H1",
  "H5_LITERAL",
  "CYCLE_TITLE_GRAMMAR",
  "MODULE_TITLE_GRAMMAR",
  // CON-874: heading numbering (Unit N / Cycle N / Module N.M), the structural sibling of
  // `EXERCISE_NUMBERING` below — one id for all three levels, since the finding's message names the
  // level and the fix is a single producer.
  "HEADING_NUMBERING",
  "UNKNOWN_LABEL",
  "BOLD_LABEL_GLUED",
  "EXERCISE_ITEM_GRAMMAR",
  "EXERCISE_BODY_INDENT",
  "EXERCISE_NUMBERING",
  "BODY_IS_CODE_BLOCK",
  "GAP_MARKUP_FORM",
  // Door 1, restored (CON-744): the two closed sets the deleted wire `Schema` used to enforce at
  // decode — see `rules/closed-sets.ts`. `BAD_STAGE` below is the third such set and never went
  // through the wire, which is why it sits in door 3 rather than here.
  "CEFR_SUBLEVEL_UNKNOWN",
  "EXERCISE_SUBTYPE_UNKNOWN",
  // Door 2 — the parse boundary (`lint.ts`'s `mapParseError`; `SINGLE_H1` above is its other id).
  "STRUCTURAL_ORDER",
  // Door 3 — the two checks written directly in `lint.ts`, which the wire projection cannot see.
  "BAD_STAGE",
  "MISSING_REQUIRED_FIELD",
  // Door 4 — the `AUDIT_RULES` registry (`rules/exercise-audits.ts`), reached by `auditCurriculum`,
  // NOT by `lintCurriculum` (CON-744). Deliberately a separate entry point: these enforce authored
  // CONTENT constraints sourced from `docs/exercise-audit-checks.md`, whereas doors 1-3 enforce the
  // markdown dialect spec. Folding them into `lintCurriculum` would silently change what every
  // existing consumer of the diagnostics rail sees; they live in the same package because they read
  // the same `ParsedCurriculum` and share the rule/finding machinery, not because they are one suite.
  "GAP_ANSWER_NOT_TARGET",
  "UNRESOLVED_GAP",
  "DIALOGUE_TOO_FEW_SPEAKERS",
  "UNREADABLE_SPEAKER_LABEL",
  "SORTWORD_GAP_EXCEEDS_TARGET",
  "NON_DIALOGUE_SHAPE_IN_DIALOGUE",
  "SORTWORD_TILES_OVER_CAP",
  "SORTWORD_TILES_UNDER_FLOOR",
  "SORTWORD_TILE_TOO_LONG",
  "DISTRACTOR_CASE_MISMATCH",
  "FORBIDDEN_TERM",
  "TUTOR_OPTION_HAS_MARKDOWN",
  "LEARNER_TOKEN_OUTSIDE_VOCAB",
  "DUPLICATE_LEARNING_TARGET",
  "EXACT_CONTENT_DUPLICATE",
  "CARRIER_ANSWER_LANGUAGE_MISMATCH",
  // CON-904, restored to TypeScript by CON-1315: the pedagogy-cluster rule family, ported from
  // `exercise_audits.registry`'s wire-format `CHECKS` onto the authored `CurriculumNode` fields
  // carrying the same information pre-generation. Formerly ran Python-side
  // (`exercise_audits/authored/rules_pedagogy.py`, now deleted) via Pyodide; all twelve ids are now
  // registered as `Rule`s in `EXERCISE_AUDIT_RULES` (`rules/exercise-audits.ts`), same door as the
  // block above — including `GENDER_FORM_DISAMBIGUATION`, whose detect reads only a hardcoded
  // L2-form set and a regex, not `persona-config`, so it was never actually coupled to the
  // gender-lexicon port blocking `INTRODUCTION_GENDER_AGREEMENT`/`PREDICATE_ADJECTIVE_GENDER_AGREEMENT`
  // below.
  "LEXICON_PRIMARY_NOT_CAPITALIZED",
  "LEXICON_CHOOSE_PRIMARY_NOT_CAPITALIZED",
  "LEXICON_CHOOSE_SECONDARY_NOT_CAPITALIZED",
  "REGISTER_FORM_DISAMBIGUATION",
  "GENDER_FORM_DISAMBIGUATION",
  "NUMBER_FORM_DISAMBIGUATION",
  "TRANSLATION_EXTRA_GLOSSES",
  "TUTOR_BEAT_CHAR_CAP",
  // Studio-native, no Python original: a tutor `check:` line authored with `[BLANK]`/`[GAP]` or no
  // `___` blank at all.
  "TUTOR_CHECK_BLANK",
  "SORTWORD_NO_DISTRACTORS",
  "SORTWORD_UNDERSIZED_ANSWER",
  "DIALOGUE_DUPLICATE_LINE",
  "RENDERED_CHOICE_LANGUAGES",
  // CON-893 re-audit follow-up: ported from `exercise_audits.checks`' wire-format
  // `_check_curriculum_subtype_payload_complete` (split into four ids, one per distinct
  // defect) and `_check_translation_no_l2_leakage`. Runs Python-side
  // (`exercise_audits/authored/rules_curriculum.py`) via Pyodide, same as the CON-904 family
  // above.
  "CLOZE_MISSING_CARRIER",
  "CLOZE_MISSING_SOLUTION",
  "MATCH_TOO_FEW_TARGETS",
  "CHOOSE_MISSING_DISTRACTORS",
  "TRANSLATION_NO_L2_LEAKAGE",
  // CON-924 (surfaced by CON-1216), restored to TypeScript by CON-1315 (Decision 10):
  // `rules_gender.py`'s per-record cluster, same family as `GENDER_FORM_DISAMBIGUATION` above.
  // Formerly blocked on a `persona-config` port (the Pyodide sandbox's own substrate); now
  // registered as `Rule`s in `GENDER_AUDIT_RULES` (`rules/gender-audits.ts`) against the
  // document's pack cast, same door as the block above.
  "INTRODUCTION_GENDER_AGREEMENT",
  "PREDICATE_ADJECTIVE_GENDER_AGREEMENT",
  // Door 4, second family — the `CURRICULUM_AUDIT_RULES` registry
  // (`rules/curriculum-audits.ts`), sourced from the inventory's §6 `CURRICULUM_CHECKS`. Same door
  // and same entry point as the exercise family above; a separate registry because the two answer
  // about different things — the exercise family judges one authored exercise, this one judges the
  // unit plan the document IS (`run_curriculum_audit` takes unit plans, and a studio curriculum
  // document converts to exactly one).
  "MODULE_HAS_NO_EXERCISES",
  "TUTOR_MISSING_AUTHORED_SCRIPT",
  "MISSING_DESCRIPTION",
  "DESCRIPTION_TOO_LONG",
  // Door 4, third family — the `SKELETON_AUDIT_RULES` registry (`rules/skeleton-audits.ts`), the
  // one-id CON-1315 restoration of `rules_skeleton.py`'s curriculum-level progression check. Same
  // door and entry point as the two families above; its own registry because it judges the
  // unit-to-unit progression across the whole curriculum, not one exercise or one unit plan.
  "WEAK_PROGRESSION_STALLED_CYCLES",
  // Door 4, fourth family — the `TITLE_LENGTH_AUDIT_RULES` registry
  // (`rules/title-length-audits.ts`, CON-1315, Decision 15, U12). Same door and entry point as the
  // three families above; its own registry because its provenance is neither §6 nor either Pyodide
  // path but `markdown_to_unit_plan_deterministic.py`'s `_cap()` truncation — a title this rule has
  // no §6/`rules_skeleton`/`rules_gender` lineage to share, so it cannot join `CURRICULUM_AUDIT_RULES`
  // without falsifying that array's own §6 row-count invariant.
  "TITLE_TOO_LONG",
  // Door 4, fifth family — the `CURRICULUM_CASCADE_RULES` registry (`rules/curriculum-audits.ts`,
  // audit-gap plan Unit A3a). Same door and entry point as the four families above; its own registry
  // for the same reason as `TITLE_LENGTH_AUDIT_RULES`: a deterministic cycle/unit-level cascade over
  // `MODULE_HAS_NO_EXERCISES`'s own already-computed per-module fact, with no §6 row of its own, so
  // it cannot join `CURRICULUM_AUDIT_RULES` without falsifying that array's §6 row-count invariant.
  "CYCLE_HAS_NO_EXERCISES",
  "UNIT_HAS_NO_EXERCISES",
  // Door 4, sixth family — the `CAN_DO_AUDIT_RULES` registry (`rules/can-do-audits.ts`, CON-2105).
  // Same door and entry point as the five families above; its own registry because its provenance
  // is the converter's focal can-do id read (`md_parse.py`'s `_CANDO_LINE_RE`), with no §6 row.
  "FOCAL_CAN_DO_ID_MISSING",
  "CAN_DO_MARKER_MALFORMED",
  // Door 4, eighth family — `LANGUAGE_CHECK_SKIP_RULES` (`rules/language-check-skips.ts`, CON-2581):
  // a language check the document's pack does not let run, reported instead of passing silently.
  "LANGUAGE_CHECK_SKIPPED",
] as const;

export const FindingId = Schema.Literal(...ALL_FINDING_IDS);
export type FindingId = typeof FindingId.Type;

// The fix-dispatch rail's `AgentTurnInput.fixScope.ruleId` (CON-1334, widened CON-1563-follow-up)
// admits two ids `FindingId` does not carry: `EXERCISE_MISSING_GENERATED_FIELD` and
// `CONVERSION_MISSING_AUTHORED_CONTENT`, both `ConversionFinding.rule_id`s (`packages/ports`'s
// `convert()`), never a lint/audit `Finding.ruleId`. A closed-union addition of exactly these two
// literals, not a widen to `Schema.String` — `fixScope` rides as unfenced text into the
// managed-agent prompt (`managed-agents-harness.ts`'s `commandPrompt`), so the wire boundary keeps
// rejecting arbitrary strings. Exported once here so `packages/ports/src/agent.ts` and
// `collab.ts`'s `FixScope.ruleId` share one union instead of each re-typing the same literals and
// risking drift.
export const FixScopeRuleId = Schema.Union(
  FindingId,
  Schema.Literal("EXERCISE_MISSING_GENERATED_FIELD"),
  Schema.Literal("CONVERSION_MISSING_AUTHORED_CONTENT"),
);
export type FixScopeRuleId = typeof FixScopeRuleId.Type;

// A single lint finding (KTD8): everything a caller needs to report or fix a departure from the
// dialect spec, without re-reading the source. Positions are NOT cached against a stale offset
// (ADR-0048) — every finding here is computed fresh from the text handed to `lintCurriculum` in
// the same call, so there is nothing to invalidate.
//
// This is core's shared deterministic-finding type — the home ADR-0087 D1 asked for and that this
// unit (U2) finally gives it, upstreamed out of `curriculum-md` rather than parked in a new shared
// package.
//
// `specSection` used to be `SpecSection` alone, with this comment recording that a pedagogy check
// "cannot fill it honestly" and that widening it "would gut the coverage test that makes the
// citation worth emitting". CON-744 revisits that, and the distinction the original reasoning
// missed is worth stating precisely, because it is what makes the widening safe:
//
//   - What actually guarantees the citation is worth emitting is that its value is GREPPABLE in a
//     real document the receiving agent holds. That is what `spec-coverage.test.ts` asserts.
//   - What the original wording assumed is that there is exactly ONE such document.
//
// The exercise-audit rules have a real second one — `docs/exercise-audit-checks.md`, the canonical
// check inventory (guarded by the `audit-lifecycle` skill). So `specSection` is now `Citation`, a
// union over two closed sets, each asserted greppable in its own document by its own coverage test.
// The guarantee is unchanged; only the assumption that one document could serve every deterministic
// producer is retired. A citation with no document behind it remains exactly as illegal as before —
// that, not the arity of the union, was always the fence.
//
// The field keeps the name `specSection` rather than being renamed to `citation`: the rename touches
// 14 files of otherwise-untouched code and is not needed for the type to be honest. Left as a
// separate, mechanical follow-up.
export const Finding = Schema.Struct({
  // The registry rule id (e.g. `EXERCISE_BODY_INDENT`), or one of the ad hoc ids `lint.ts` emits
  // for a defect with no registry rule of its own (R5) — see its checks written directly against
  // the Model. Closed over `ALL_FINDING_IDS` so a downstream table keyed by id is
  // exhaustiveness-checked by `tsc` rather than by hope.
  ruleId: FindingId,
  severity: Severity,
  message: Schema.String,
  // The section this finding enforces, worded exactly as its source document's own heading so the
  // receiving agent can grep for it — the bundled structure spec for a dialect rule, the audit
  // inventory for an exercise-audit rule.
  specSection: Citation,
  // What the rule requires, in one sentence — the same prose as the registry entry's `docs`, carried
  // on the finding so a caller reporting or fixing it never has to reach back into `RULES`.
  docs: Schema.String,
  // Curriculum -> Unit -> Cycle -> Module titles enclosing this finding, shortest-first from the
  // document root, truncated to whatever level of the tree the finding was found at.
  headingPath: Schema.Array(Schema.String),
  line: Schema.Number,
  column: Schema.Number,
  // The verbatim source text the finding points at (a line, or a matched span within one).
  span: Schema.String,
  // Whether a `fix` exists for this rule. U4 registers the flag; U5 supplies the fix engine.
  fixable: Schema.Boolean,
});
export type Finding = typeof Finding.Type;
